import { useState, useEffect, useRef, useSyncExternalStore } from 'react';
import type { ApiClients } from '../context/ApiClientsContext';
import { getContextViewPermissions, type ContextViewPermissions } from '../api/contextApi';
import {
  subscribeToPermissionInvalidation,
  getPermissionEpoch,
  bumpPermissionEpoch,
  identifyClient,
  registerPermissionCoalescingMap,
} from './permissionInvalidation';

export interface UseContextViewPermissionsReturn extends ContextViewPermissions {
  loading: boolean;
  error: string | null;
}

const FAIL_CLOSED: ContextViewPermissions = { canEdit: false, canDelete: false, canPublish: false };

interface InFlightEntry {
  /**
   * The `schemaClient` the request was issued with, compared by REFERENCE.
   *
   * This is NOT what makes an impersonation switch safe, despite the obvious
   * reading. `ApiClientsProvider` deliberately does not rebuild its clients on
   * impersonation — the bearer token is resolved per request through
   * `getEffectiveToken`, so the reference is stable across identities and this
   * check would never fire for that case. Cross-identity safety comes from
   * `resetContextViewPermissionsCache` bumping the epoch below.
   *
   * What the reference check does buy: it stops a request issued by a client
   * that has since been REBUILT (logout, tenant change) from being adopted by
   * a later mount. `String(client)` is `'[object Object]'` for every instance,
   * so the client cannot be serialised into this map's key directly — the
   * reference comparison is the cheap way to say the same thing here. Where a
   * serialisable form IS needed (the per-mount resolution key), `identifyClient`
   * below assigns a `WeakMap` ordinal; the two are the same notion of client
   * identity expressed for two different containers.
   */
  client: unknown;
  request: Promise<ContextViewPermissions>;
}

// Module-scope: dedupes CONCURRENT fetches for one view so two mounts issue one
// request, not two. Entries are removed as soon as the request settles, so this
// holds no answers between renders — it is a request coalescer, not a result
// cache. The decisions themselves live in each mount's state, which is why
// invalidation needs the epoch below and not just a clear of this map.
const inFlightByView = new Map<string, InFlightEntry>();
// Registered so ANY epoch bump drops these requests, not just this hook's own
// reset — a shared epoch means another hook's reset re-runs this hook's effect.
registerPermissionCoalescingMap(inFlightByView);

/** A permission decision together with the exact key it was resolved FOR. */
interface ResolvedDecision {
  key: string;
  permissions: ContextViewPermissions;
  error: string | null;
}

/**
 * Invalidates every mounted caller's decision and drops in-flight requests.
 * Call on logout / impersonation switch, and whenever the server refuses a
 * write the cached decision said was permitted.
 *
 * The epoch lives in `./permissionInvalidation` and is shared with
 * `useContextPermissions` — see that module for why clearing `inFlightByView`
 * alone cannot invalidate anything, and why the epoch is shared rather than
 * per-hook. The map is cleared BEFORE the bump so the re-fetch the notification
 * triggers cannot adopt a request issued under the old identity.
 */
export function resetContextViewPermissionsCache(): void {
  inFlightByView.clear();
  bumpPermissionEpoch();
}

/**
 * Fetches permission decisions for the current user on a context view.
 *
 * Fail-closed: any network or SpiceDB error results in all-false permissions
 * so the UI conservatively disables edit/delete/publish actions rather than
 * exposing them.
 *
 * Follows `useCanEditContext`'s shape, but fixes its two gaps: this hook is
 * re-exported from the hooks barrel, and it dedupes concurrent fetches for
 * the same view via a module-scope cache.
 *
 * Requires the backend route (org scoped server-side from the token):
 *   GET /context-views/system_name/:systemName/permissions
 */
export function useContextViewPermissions(
  apiClients: ApiClients | null,
  viewSystemName: string | null | undefined
): UseContextViewPermissionsReturn {
  /**
   * The decision AND the key it was resolved for, in ONE state value.
   *
   * They are deliberately not two `useState`s. `loading` is derived from the
   * key, so any path that writes one without the other lets the hook report
   * "settled" against a value that was never resolved for the current key —
   * which is exactly what a separate `permissions` + `resolvedFor` pair did:
   * blanking `permissions` to FAIL_CLOSED before a fetch left `resolvedFor`
   * matching, so switching view A → B → A before B settled reported
   * `{ loading: false, canEdit: false }` — "no permission" rather than
   * "checking…", the very confusion D-3 exists to prevent. Holding both in one
   * value makes that unrepresentable.
   *
   * Deriving `loading` (rather than tracking a flag) is what closes the two
   * gaps a `useState(false)` flag left open:
   *
   *  - it reported `{ loading: false, canEdit: false }` on the first committed
   *    render, so `deriveViewSaveReason` said "no permission" instead of
   *    "checking…" on exactly the frame D-3 exists to fix; and
   *  - state updates in an effect land AFTER the render that changed the view
   *    or bumped the epoch commits, so that render returned the PREVIOUS
   *    view's decision with `loading: false` — one frame of fail-open in a
   *    permission hook.
   *
   * A key mismatch is visible during the render that causes it.
   */
  const [decision, setDecision] = useState<ResolvedDecision | null>(null);

  const isMountedRef = useRef(true);
  const epoch = useSyncExternalStore(
    subscribeToPermissionInvalidation,
    getPermissionEpoch,
    getPermissionEpoch
  );

  const schemaClient = apiClients?.schemaClient ?? null;
  const hasTarget = Boolean(schemaClient && viewSystemName);
  /**
   * JSON-encoded tuple rather than concatenation: unambiguous whatever
   * characters a system name contains.
   *
   * The CLIENT is part of the identity, not just the view and the epoch. All
   * three re-trigger the decision, so leaving the client out let a rebuilt
   * client (logout, tenant change, account switch — `ApiClientsProvider`
   * rebuilds on `accountKey`) re-run the effect while the key stayed equal,
   * so the render reported the PREVIOUS identity's decision as settled. The
   * epoch covers impersonation, which does NOT rebuild the client; it does not
   * cover the cases that do.
   */
  const resolutionKey =
    hasTarget && schemaClient
      ? JSON.stringify([identifyClient(schemaClient as object), viewSystemName, epoch])
      : null;

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!schemaClient || !viewSystemName) {
      setDecision(null);
      return;
    }

    let cancelled = false;
    // Nothing is blanked before the fetch. A stale decision cannot be surfaced
    // anyway — its key no longer matches, so the derived `loading` below
    // returns FAIL_CLOSED for it — and blanking the value while leaving the key
    // in place is precisely what made a matching key stop implying "resolved
    // for this key". Switching from a view the caller can edit to one they
    // cannot is covered by the key changing, in the render that changes it.
    const existing = inFlightByView.get(viewSystemName);
    // Reference equality on the CLIENT, not a serialised key — see the
    // `InFlightEntry.client` docblock for why this is not the impersonation
    // guard it looks like.
    let request = existing?.client === schemaClient ? existing.request : undefined;
    if (!request) {
      const issued = getContextViewPermissions(schemaClient, viewSystemName);
      request = issued;
      inFlightByView.set(viewSystemName, { client: schemaClient, request: issued });
      // Side branch for cache cleanup only — swallow here so a rejection
      // doesn't surface as an unhandled rejection; the `request` variable
      // below still carries the real error to each mount's own .catch().
      issued
        .finally(() => {
          // Only clear the entry we created; a newer client's request may have
          // replaced it while this one was still settling.
          if (inFlightByView.get(viewSystemName)?.request === issued) {
            inFlightByView.delete(viewSystemName);
          }
        })
        .catch(() => undefined);
    }

    request
      .then((result) => {
        if (!cancelled && isMountedRef.current && resolutionKey) {
          setDecision({ key: resolutionKey, permissions: result, error: null });
        }
      })
      .catch((err: unknown) => {
        if (!cancelled && isMountedRef.current && resolutionKey) {
          // A refusal IS a resolved answer — settle, or the caller reads
          // `loading: true` forever and never learns the request finished.
          setDecision({
            key: resolutionKey,
            permissions: FAIL_CLOSED,
            error: err instanceof Error ? err.message : 'Failed to fetch context view permissions',
          });
        }
      });

    return () => {
      cancelled = true;
    };
    // `epoch` is not read in the body — it is the invalidation signal that
    // re-runs this effect when `resetContextViewPermissionsCache` fires.
    // `resolutionKey` is derived from the client, `viewSystemName` and `epoch`,
    // so it adds no new trigger.
  }, [schemaClient, viewSystemName, epoch, resolutionKey]);

  // Derived, never stored: a key mismatch means the decision belongs to a
  // different view, a different client, or a pre-invalidation identity, so it
  // must not be surfaced even for the single render before the effect re-runs.
  const resolved = hasTarget && decision?.key === resolutionKey ? decision : null;

  return {
    ...(resolved ? resolved.permissions : FAIL_CLOSED),
    loading: hasTarget && !resolved,
    error: resolved ? resolved.error : null,
  };
}

export default useContextViewPermissions;
